import { createRouter, createWebHistory } from "vue-router";
const LandingView = () => import("../views/LandingView.vue");
const CoverageView = () => import("../views/CoverageView.vue");
const WhyUsView = () => import("../views/WhyUsView.vue");
const PrivacyPolicy = () => import("../views/PrivacyPolicy.vue");
const TermsAndConditions = () => import("../views/TermsAndConditions.vue");
const AboutView = () => import("../views/AboutCompanyView.vue");
const ServicesView = () => import("../views/ServicesLandingView.vue");
const ContactView = () => import("../views/ContactView.vue");
const EngineerNetworkView = () => import("../views/EngineerNetworkView.vue");

const routes = [
  {
    path: "/why-us",
    name: "why-us",
    component: WhyUsView,
    meta: {
      title: "Why WizzTech - IT Support That Works With Your Team",
      description:
        "A clear process, trained people and proper records for every IT job. See why teams choose WizzTech for hands-on IT support worldwide.",
    },
  },
  {
    path: "/coverage",
    name: "coverage",
    component: CoverageView,
    meta: {
      title: "Global IT Coverage - WizzTech",
      description:
        "Supporting teams across Asia, Europe, EMEA, the USA, Australia and New Zealand. Check coverage for your location with WizzTech.",
    },
  },
  {
    path: "/",
    alias: "/landing",
    name: "landing",
    component: LandingView,
    meta: {
      title: "WizzTech - Hands-on IT, Worldwide",
      description:
        "Device onboarding, secure offboarding and WiFi site surveys for teams across the world. Get a quote from WizzTech.",
    },
  },
  {
    path: "/contact",
    name: "contact",
    component: ContactView,
    meta: {
      title: "Contact Us - WizzTech",
      description: "Contact WizzTech to discuss your business IT needs.",
    },
  },
  {
    path: "/engineer-network",
    name: "engineer-network",
    component: EngineerNetworkView,
    meta: {
      title: "Join Our Engineer Network - WizzTech",
      description:
        "Apply to join WizzTech's network of skilled field engineers and technicians.",
    },
  },
  {
    path: "/services",
    name: "services",
    component: ServicesView,
    meta: {
      title: "IT Services - WizzTech",
      description:
        "Employee device onboarding, secure offboarding and WiFi site surveys. WizzTech provides practical IT services for teams worldwide.",
    },
  },
  ...[
    "/services/software-development",
    "/services/web-development",
    "/services/data-analytics",
    "/services/ai-automation",
    "/services/it-infrastructure",
    "/services/devops",
    "/services/it-support-help-desk",
    "/services/remote-it-support",
    "/services/managed-it-services",
    "/services/it-staffing-outsourcing",
  ].map((path) => ({ path, redirect: "/services" })),
  {
    path: "/terms-and-conditions",
    name: "terms-and-conditions",
    component: TermsAndConditions,
    meta: {
      title: "Terms & Conditions - WizzTech",
      description:
        "Read the terms for using the WizzTech website and sending a service inquiry.",
    },
  },
  {
    path: "/privacy-policy",
    name: "privacy-policy",
    component: PrivacyPolicy,
    meta: {
      title: "Privacy Policy - WizzTech",
      description:
        "Learn how WizzTech handles information shared through our website and service inquiries.",
    },
  },
  {
    path: "/home",
    redirect: "/",
  },
  {
    path: "/product/:id",
    redirect: "/services",
  },
  {
    path: "/about",
    name: "about",
    component: AboutView,
    meta: {
      title: "About WizzTech - Practical IT Services",
      description:
        "Learn how WizzTech combines software development, IT support, operations, and technical resources to help businesses move forward.",
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});
router.beforeEach((to, from, next) => {
  if (to.hash === "#landing-why-us" && ["/", "/landing"].includes(to.path)) {
    next({ path: "/why-us", query: to.query, replace: true });
    return;
  }
  if (!to.hash) {
    window.scroll(0, 0);
  }
  next();
});
router.afterEach((to) => {
  const defaultTitle = "Wizz Tech - Turning Ideas into Digital Reality";
  const defaultDescription =
    "Wizz Tech builds scalable digital products, AI solutions, web and mobile applications, product design, and engineering services.";
  const title = to.meta.title || defaultTitle;
  const description = to.meta.description || defaultDescription;
  const canonicalPath = to.path === "/" ? "/" : to.path.replace(/\/$/, "");
  const canonicalUrl = `https://wizztech.us${canonicalPath}`;

  document.title = title;

  updateMetaTag("name", "description", description);
  updateMetaTag("property", "og:title", title);
  updateMetaTag("property", "og:description", description);
  updateMetaTag("property", "og:url", canonicalUrl);
  updateMetaTag("name", "twitter:title", title);
  updateMetaTag("name", "twitter:description", description);
  updateCanonical(canonicalUrl);

  if (to.hash) {
    scrollToHash(to.hash);
  }
});

function scrollToHash(hash, attempt = 0) {
  const element = document.querySelector(hash);

  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }

  if (attempt < 10) {
    setTimeout(() => scrollToHash(hash, attempt + 1), 100);
  }
}

function updateMetaTag(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function updateCanonical(href) {
  let element = document.head.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
}

export default router;
